import type { FunctionComponent } from 'react';
import LinesEllipsis from 'react-lines-ellipsis';
import cx from 'classnames';

import { useMemberId } from '@trello/authentication';
import type { Preview } from '@trello/image-previews';
import type { RouterLinkProps } from '@trello/router/router-link';
import { RouterLink } from '@trello/router/router-link';
import { boardUrlFromShortLink } from '@trello/urls';

import { BoardTemplateBadge } from './BoardTemplateBadge';
import { getBackgroundStyle } from './BoardTile';
import { useBoardTileLargeFragment } from './BoardTileLargeFragment.generated';
import { useBoardTileStarsFragment } from './BoardTileStarsFragment.generated';
import { BoardVisibilityBadge } from './BoardVisibilityBadge';
import { StarredBoardButton } from './StarredBoardButton';

import styles from './BoardTileLarge.less';

interface BoardTileLargeProps extends Omit<RouterLinkProps, 'onClick'> {
  boardId: string;
  name: string;
  url: string;
  boardStarId?: string;
  onStarClick?: () => void;
  onBoardClick?: () => void;
  isTemplate?: boolean;
  backgroundBrightness?: 'dark' | 'light' | 'unknown' | null;
  backgroundColor?: string;
  backgroundImage?: string;
  backgroundImageScaled?: Preview[];
  permissionLevel: string;
  backgroundTile?: boolean;
  showStar?: boolean;
}

export const BoardTileLargeUnconnected: FunctionComponent<
  BoardTileLargeProps
> = ({
  boardId,
  name,
  url,
  className,
  isTemplate,
  backgroundBrightness,
  backgroundColor,
  backgroundImage,
  backgroundImageScaled,
  backgroundTile,
  boardStarId,
  onStarClick,
  onBoardClick,
  permissionLevel,
  showStar,
  ...props
}) => {
  const tileBackgroundStyle = getBackgroundStyle(
    backgroundTile,
    backgroundImage,
    backgroundColor,
    backgroundImageScaled,
  );

  return (
    <div
      className={cx(styles.boardTile, className)}
      style={tileBackgroundStyle}
    >
      <RouterLink
        href={url}
        title={name}
        onClick={onBoardClick}
        className={styles.link}
        {...props}
      >
        <div
          className={cx(styles.boardTileOverlay, {
            [styles.dark]: backgroundBrightness !== 'light',
            [styles.light]: backgroundBrightness === 'light',
          })}
        />
        <div className={styles.boardDetails}>
          <div className={styles.boardName}>
            <LinesEllipsis maxLine={2} text={name} />
          </div>
          <div className={styles.badges}>
            <div className={styles.visiblityContainer}>
              {isTemplate && <BoardTemplateBadge showIcon />}
              <BoardVisibilityBadge visibility={permissionLevel} />
            </div>
            {showStar && (
              <div
                className={cx(styles.starContainer, {
                  [styles.isStarred]: !!boardStarId,
                })}
              >
                <StarredBoardButton
                  idBoard={boardId}
                  boardStarId={boardStarId}
                  maxBoardStarPosition={0}
                  onClick={onStarClick}
                  name={name}
                />
              </div>
            )}
          </div>
        </div>
      </RouterLink>
    </div>
  );
};

interface BoardTileLargeConnectedProps
  extends Omit<RouterLinkProps, 'onClick'> {
  boardId: string;
  onStarClick?: () => void;
  onBoardClick?: () => void;
}

export const BoardTileLarge: FunctionComponent<
  BoardTileLargeConnectedProps
> = ({ boardId, onBoardClick, onStarClick, ...props }) => {
  const memberId = useMemberId();
  const { data: board } = useBoardTileLargeFragment({
    from: { id: boardId },
    returnPartialData: true,
  });
  const { data: member } = useBoardTileStarsFragment({
    from: { id: memberId },
  });

  if (!board) {
    return null;
  }

  const boardStarId =
    member && board.starred
      ? (member.boardStars ?? []).find((star) => star.idBoard === board.id)?.id
      : undefined;

  return (
    <BoardTileLargeUnconnected
      boardId={board.id}
      name={board.name}
      isTemplate={board.prefs?.isTemplate}
      backgroundBrightness={board.prefs?.backgroundBrightness}
      backgroundColor={board.prefs?.backgroundTopColor}
      backgroundImage={board.prefs?.backgroundImage ?? undefined}
      backgroundImageScaled={board.prefs?.backgroundImageScaled ?? undefined}
      backgroundTile={board.prefs?.backgroundTile}
      boardStarId={boardStarId}
      url={boardUrlFromShortLink(board.shortLink, board.name)}
      permissionLevel={board.prefs?.permissionLevel!}
      onBoardClick={onBoardClick}
      onStarClick={onStarClick}
      // Do not show start button if logged out member
      showStar={!!member}
      {...props}
    />
  );
};
